<style>
.unit-box {
  border-radius: 10px;
  background-color: rgb(235, 235, 235);
  padding: 10px;
}
</style>

<template>
  <div>
    <h1>{{ localization["Register As"][ctx.language] }} {{ userType.text }}</h1>

    <v-form
      v-if="newUser == null && userType.id == 'patient'"
      id="registerForm"
      ref="form"
      v-model="validForm"
      @submit.prevent
    >
      <v-layout row>
        <v-flex
          md4
        >
          <v-text-field
            id="firstname"
            v-model="firstname"
            :rules="[rules.required, rules.noWhiteSpace, rules.lettersAndNumbersOnly]"
            :label="`${localization['First Name'][ctx.language]}`"
          />
        </v-flex>
        <v-flex
          md4
        >
          <v-text-field
            id="lastname"
            v-model="lastname"
            :rules="[rules.required, rules.noWhiteSpace, rules.lettersAndNumbersOnly]"
            :label="`${localization['Last Name'][ctx.language]}`"
          />
        </v-flex>
      </v-layout>

      <v-menu
        ref="menu"
        :closeOnContentClick="false"
        transition="scale-transition"
        offsetY
        minWidth="auto"
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            v-model="birthdate"
            :rules="[rules.required]"
            :label="`${localization['Birthdate'][ctx.language]}`"
            v-on="on"
            @keydown.capture="$event.preventDefault()"
          />
        </template>
        <v-date-picker
          ref="picker"
          v-model="birthdate"
          :max="new Date().toISOString().substr(0, 10)"
          color="#4EBFFF"
          min="1900-01-01"
          @change="saveBirthdate"
        />
      </v-menu>

      <div class="unit-box">
        <v-btn-toggle
          v-model="units"
          mandatory

        >
          <v-btn
            value="metric"
            style="font-size: 12px;"> Metric </v-btn>
          <v-btn
            value="imperial"
            style="font-size: 12px;"> Imperial </v-btn>
        </v-btn-toggle>

        <v-layout row>
          <v-flex
            md6
          >
            <v-text-field
              v-if="units === 'metric'"
              id="height"
              v-model.number="height"
              :rules="[rules.required]"
              :label="`${localization['Height'][ctx.language]}`"
              type="number"
            />

            <v-layout
              v-if="units === 'imperial'"
              row>
              <v-text-field
                id="height-feet"
                v-model.number="heightFeet"
                :rules="[rules.required && rules.numbersOnly]"
                :label="`Height - feet`"
                type="number"
              >
                <template v-slot:label>
                  <span style="font-size: 12px;">
                    Height [ft]
                  </span>
                </template>
              </v-text-field>
              <v-text-field
                id="height-inches"
                v-model.number="heightInches"
                :rules="[rules.required && rules.numbersOnly]"
                type="number"
                style="margin-right: 15px;"
              >
                <template v-slot:label>
                  <span style="font-size: 12px;">
                    Height [in]
                  </span>
                </template>
              </v-text-field>
            </v-layout>
          </v-flex>
          <v-flex
            md4
          >
            <v-text-field
              id="weight"
              v-model.number="weight"
              :rules="[rules.required && rules.numbersOnly]"
              :label="`${(units === 'metric') ? localization['Weight'][ctx.language] : 'Weight [lbs]'}`"
              type="number"
            />
          </v-flex>
        </v-layout>
      </div>
      <v-autocomplete
        id="biologicalsex"
        v-model="biologicalsex"
        :items="biologicalsexSelection(ctx.language)"
        :rules="[rules.required]"
        :label="`${localization['Sex'][ctx.language]}`"
        :placeholder="localization['Choose Sex'][ctx.language]"
      />

      <v-text-field
        id="email"
        v-model="email"
        :rules="[rules.email, rules.required]"
        label="E-mail"
      />

      <Password
        v-model="password"
        :confirmation="true"
        :label="`${localization['Password'][ctx.language]}`"
        :language="ctx.language"/>

      <v-autocomplete
        id="developmentok"
        v-model="developmentok"
        :rules="[rules.required]"
        :items="developmentokSelection"
        :label="`${localization['Anonymous'][ctx.language]}`"
        :placeholder="localization['Choose One'][ctx.language]"
        itemText="message"
        itemValue="ok"
        autowidth
      />

      <v-select
        id="hosting"
        v-model="hosting"
        :items="availableHostings"
        :rules="[rules.required]"
        :label="`${localization['Data Storage Location'][ctx.language]}`"
        itemText="description"
        returnObject
      />

      <v-btn
        id="submitButton"
        :disabled="!validForm || submitting"
        @click="submit"
      >{{ localization['Create'][ctx.language] }}</v-btn
      >

      <v-btn
        id="clearButton"
        @click="clear">{{ localization['Clear'][ctx.language] }}</v-btn>

    </v-form>

    <v-form
      v-if="newUser == null && userType.id == 'doctor'"
      id="registerForm"
      ref="form"
      v-model="validForm"
      @submit.prevent
    >
      <v-layout row>
        <v-flex
          md6
        >
          <v-text-field
            id="firstname"
            v-model="firstname"
            :rules="[rules.required]"
            :label="`* ${localization['First Name'][ctx.language]}`"
          />
        </v-flex>

        <v-flex
          md6
        >
          <v-text-field
            id="lastname"
            v-model="lastname"
            :rules="[rules.required]"
            :label="`* ${localization['Last Name'][ctx.language]}`"
          />
        </v-flex>
      </v-layout>

      <v-autocomplete
        id="affiliation"
        v-model="affiliation"
        :items="affiliationSelection"
        :rules="[rules.required]"
        :label="`* ${localization['Affiliation'][ctx.language]}`"
        :placeholder="localization['Choose Affiliation'][ctx.language]"
      />

      <v-layout row>
        <v-flex>
          <v-text-field
            id="street"
            v-model="street"
            :rules="[street !== '' && (rules.lettersAndNumbersOnly, rules.noWhiteSpaceWithSlash) ]"
            :label="localization['Street'][ctx.language]"
          />
        </v-flex>
      </v-layout>

      <v-layout row>
        <v-flex
          md4
        >
          <v-text-field
            id="town"
            v-model="town"
            :rules="[town !== '' && rules.lettersAndNumbersOnly]"
            :label="localization['Town'][ctx.language]"
          />
        </v-flex>
        <v-flex
          md4
        >
          <v-text-field
            id="zip"
            v-model.number="zip"
            :rules="[zip !== '' && rules.noWhiteSpace]"
            :label="localization['ZIP'][ctx.language]"
            type="number"
          />
        </v-flex>
        <v-flex
          md4
        >
          <v-text-field
            id="country"
            v-model="country"
            :rules="[country !== '' && rules.lettersOnly]"
            :label="localization['Country'][ctx.language]"
          />
        </v-flex>
      </v-layout>

      <v-text-field
        id="phoneno"
        v-model="phoneno"
        :rules="[phoneno !== '' && rules.phoneNumber]"
        :label="localization['Phone No.'][ctx.language]"
      />

      <v-text-field
        id="email"
        v-model="email"
        :rules="[rules.email, rules.required]"
        label="* E-mail"
      />

      <Password
        v-model="password"
        :confirmation="true"
        :label="`* ${localization['Password'][ctx.language]}`"
        :language="ctx.language"/>

      <v-btn
        id="submitButton"
        :disabled="!validForm || submitting"
        @click="submit"
      >{{ localization['Create'][ctx.language] }}</v-btn
      >

      <v-btn
        id="clearButton"
        @click="clear">{{ localization['Clear'][ctx.language] }}</v-btn>

    </v-form>

    <v-form
      v-if="newUser == null && (userType.id == 'analysis' || userType.id == 'statistician')"
      id="registerForm"
      ref="form"
      v-model="validForm"
      @submit.prevent
    >
      <v-text-field
        id="email"
        v-model="email"
        :rules="[rules.email, rules.required]"
        label="E-mail"
        placeholder=""
      />

      <Password
        v-model="password"
        :confirmation="true"
        :label="localization['Password'][ctx.language]"
        :language="ctx.language"/>

      <v-btn
        id="submitButton"
        :disabled="!validForm || submitting"
        @click="submit"
      >{{ localization['Create'][ctx.language] }}</v-btn
      >

      <v-btn
        id="clearButton"
        @click="clear">{{ localization['Clear'][ctx.language] }}</v-btn>

    </v-form>

    <v-form
      v-if="newUser == null && userType.id == 'nushu_x_user'"
      id="registerForm"
      ref="form"
      v-model="validForm"
      @submit.prevent
    >
      <v-layout row>
        <v-flex>
          <v-text-field
            id="firstname"
            v-model="firstname"
            :rules="[rules.required, rules.noWhiteSpace, rules.lettersOnly]"
            :label="`${localization['First Name'][ctx.language]}`"
          />
        </v-flex>

        <v-flex>
          <v-text-field
            id="lastname"
            v-model="lastname"
            :rules="[rules.required, rules.noWhiteSpace, rules.lettersOnly]"
            :label="`${localization['Last Name'][ctx.language]}`"
          />
        </v-flex>
      </v-layout>

      <v-layout row>
        <v-flex>
          <v-menu
            ref="menu"
            :closeOnContentClick="false"
            transition="scale-transition"
            offsetY
            minWidth="auto"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="birthdate"
                :rules="[rules.required]"
                :label="`${localization['Birthdate'][ctx.language]}`"
                v-on="on"
                @keydown.capture="$event.preventDefault()"
              />
            </template>
            <v-date-picker
              ref="picker"
              v-model="birthdate"
              :max="new Date().toISOString().substr(0, 10)"
              color="#4EBFFF"
              min="1900-01-01"
              @change="saveBirthdate"
            />
          </v-menu>
        </v-flex>
        <v-flex>
          <v-autocomplete
            id="biologicalsex"
            v-model="biologicalsex"
            :items="biologicalsexSelection(ctx.language)"
            :rules="[rules.required]"
            :label="`${localization['Sex'][ctx.language]}`"
            :placeholder="localization['Choose Sex'][ctx.language]"
          />
        </v-flex>
      </v-layout>
      <div class="unit-box">
        <v-btn-toggle
          v-model="units"
        >
          <v-btn
            value="metric"
            style="font-size: 12px;"> Metric </v-btn>
          <v-btn
            value="imperial"
            style="font-size: 12px;"> Imperial </v-btn>
        </v-btn-toggle>

        <v-layout row>
          <v-flex
            md6
          >
            <v-text-field
              v-if="units === 'metric'"
              id="height"
              v-model.number="height"
              :rules="[rules.required]"
              :label="`${localization['Height'][ctx.language]}`"
              type="number"
            />

            <v-layout
              v-if="units === 'imperial'"
              row>
              <v-text-field
                id="height-feet"
                v-model.number="heightFeet"
                :rules="[rules.required && rules.numbersOnly]"
                :label="`Height - feet`"
                type="number"
              >
                <template v-slot:label>
                  <span style="font-size: 12px;">
                    Height [ft]
                  </span>
                </template>
              </v-text-field>
              <v-text-field
                id="height-inches"
                v-model.number="heightInches"
                :rules="[rules.required && rules.numbersOnly]"
                type="number"
                style="margin-right: 15px;"
              >
                <template v-slot:label>
                  <span style="font-size: 12px;">
                    Height [in]
                  </span>
                </template>
              </v-text-field>
            </v-layout>
          </v-flex>
          <v-flex
            md4
          >
            <v-text-field
              id="weight"
              v-model.number="weight"
              :rules="[rules.required && rules.numbersOnly]"
              :label="`${(units === 'metric') ? localization['Weight'][ctx.language] : 'Weight'}`"
              type="number"
            />
          </v-flex>
        </v-layout>
      </div>
      <v-text-field
        id="email"
        v-model="email"
        :rules="[rules.email, rules.required]"
        label="E-mail"
      />

      <Password
        v-model="password"
        :confirmation="true"
        :label="localization['Password'][ctx.language]"
        :language="ctx.language"/>

      <v-autocomplete
        id="developmentok"
        v-model="developmentok"
        :rules="[rules.required]"
        :items="developmentokSelection"
        :label="`${localization['Anonymous'][ctx.language]}`"
        :placeholder="localization['Choose One'][ctx.language]"
        itemText="message"
        itemValue="ok"
        autowidth
      />

      <v-btn
        id="submitButton"
        :disabled="!validForm || submitting"
        @click="submit"
      >{{ localization['Create'][ctx.language] }}</v-btn
      >

      <v-btn
        id="clearButton"
        @click="clear">{{ localization['Clear'][ctx.language] }}</v-btn>

    </v-form>

    <div v-if="ctx.isAccessRequest()">
      <v-divider class="mt-3 mb-2" />
      <router-link
        :to="{ name: 'Authorization' }"
      ><h3>{{ localization['Go to Sign in'][ctx.language] }}</h3></router-link
      >
    </div>

    <Alerts
      :successMsg="success"
      :errorMsg="error" />
  </div>
</template>

<script>
import Password from './bits/Password.vue';
import Alerts from './bits/Alerts.vue';
import Context from '../../context.js';
import controllerFactory from '../controller/controller.js';
import setupCalls from '../../setupcalls.json';
import userTypes from '../../userTypes.json';
import affiliations from '../../affiliations/affiliations.json';
import localization from '../../translationDict.json';

export default {
  components: {
    Password,
    Alerts,
  },
  data: () => ({
    password: '',
    email: '',
    firstname: '',
    middlename: '',
    lastname: '',
    phoneno: '',
    street: '',
    town: '',
    zip: '',
    country: '',
    birthdate: '',
    height: '',
    heightFeet: '',
    heightInches: '',
    weight: '',
    shoesize: '',
    impairedside: '',
    biologicalsex: '',
    developmentok: '',
    affiliation: null,
    hosting: null,
    availableHostings: [],
    newUser: null,
    submitting: false,
    ctx: {},
    c: null,
    error: '',
    success: '',
    validForm: false,
    debug: false,
    units: 'metric',
  }),
  computed: {
    rules: function () {
      return {
        required: (value) => !!value || this.localization['This field is required.'][this.ctx.language],
        // eslint-disable-next-line no-useless-escape
        email: value => /^((?:[a-zA-Z0-9_\-\.\+]+|::)+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/.test(value) || this.localization['E-mail must be valid.'][this.ctx.language],
        phoneNumber: value => /^[+]*[(]{0,1}[0-9]{1,3}[)]{0,1}[-\s./0-9]*$/g.test(value) || this.localization['Incorrect phone number'][this.ctx.language],
        lettersOnly: value => /[A-Za-z]$/g.test(value) || this.localization['Letters only'][this.ctx.language],
        lettersAndNumbersOnly: value => /^[a-zA-Z0-9.\-_./_ _]+$/g.test(value) || this.localization['Letters and Numbers only'][this.ctx.language],
        noWhiteSpaceWithSlash: value => /^[-a-zA-Z0-9-()./]+(\s+[-a-zA-Z0-9-()./]+)*$/.test(value) || this.localization['No white spaces'][this.ctx.language],
        noWhiteSpace: value => /^[-a-zA-Z0-9-()]+(\s+[-a-zA-Z0-9-()]+)*$/.test(value) || this.localization['No white spaces'][this.ctx.language],
        numbersOnly: value => /^[0-9]+$/.test(value) || this.localization['Numbers only'][this.ctx.language],
      };
    },
    userType: function () {
      return (userTypes[this.$route.query.type] !== undefined) ? userTypes[this.$route.query.type] : userTypes['patient'];
    },
    affiliationSelection: function () {
      const affiliationNames = Object.values(affiliations).map((v) => v.name).sort();
      return (Array.isArray(affiliationNames) && affiliationNames.length ? affiliationNames : ['Magnes']);
    },
    impairedsideSelection: function () {
      return language => [this.localization['none'][language],
        this.localization['left'][language],
        this.localization['right'][language],
        this.localization['both'][language]];
    },
    biologicalsexSelection: function () {
      return language => [this.localization['female'][language],
        this.localization['male'][language]];
    },
    developmentokSelection: function () {
      return [
        {message: this.localization['I consent'][this.ctx.language], ok: 'yes'},
        {message: this.localization['I do not consent'][this.ctx.language], ok: 'no'},
      ];
    },
  },
  async created () {
    // deep clone
    this.localization = JSON.parse(JSON.stringify(localization));
    this.setupCalls = JSON.parse(JSON.stringify(setupCalls));
    this.ctx = new Context(this.$route.query);
    await this.ctx.init();
    this.c = controllerFactory(this.ctx);
    // Fill selector with available hostings, preselect first one
    this.c
      .loadHostings()
      .then(hosts => {
        this.initHostings(hosts);
        this.hosting = hosts[0];
      })
      .catch(this.showError);
  },

  methods: {
    submit () {
      // check the age of the applicant
      if (this.getAge(this.birthdate) < 6) {
        this.error = this.localization['6 years message'][this.ctx.language];
        return;
      }

      // if no shoe size set, pass -1
      if (this.shoesize === '') {
        this.shoesize = -1;
      }

      // if no impaired side set, pass 'none'
      if (this.impairedside.trim() === '') {
        this.impairedside = 'none';
      }

      // if no impaired side set, pass 'none'
      if (this.zip === '') {
        this.zip = 0;
      }

      if (this.$refs.form.validate()) {
        this.submitting = true;

        // trim on submit
        this.firstname = this.firstname.trim();
        this.middlename = this.middlename.trim();
        this.lastname = this.lastname.trim();
        this.street = this.street.trim();
        this.town = this.town.trim();
        this.country = this.country.trim();

        // recursive function to create a user with a unique username
        this.createUniqueUser();
      }
    },
    createUniqueUser () {
      // get selected core
      let availableCore = this.hosting.availableCore;

      // generate username
      this.ctx.user.username = this.generateRandomUsername(8);

      // create user
      this.c.createUser(availableCore,
        this.password,
        this.email,
        this.getAffiliation(),
        this.hosting)
        .then(async (newUser) => {
          this.newUser = newUser;
          this.success = this.localization['New User Successful'][this.ctx.language] + `${newUser.username}.`;

          // issue a login call to create the default streams
          try {
            await this.c.login(this.password)
              .then(async (connection) => {
                // try {
                let calls = [];
                if (this.userType.id === 'patient') {
                  calls = this.setupCalls.patient;

                  // name
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'name',
                      ],
                      'type': 'contact/magnes-v1',
                      'content': {
                        'first-name': this.firstname,
                        'middle-name': this.middlename,
                        'last-name': this.lastname,
                      },
                    },
                  });

                  // birthdate
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'birthdate',
                      ],
                      'type': 'date/iso-8601',
                      'content': this.birthdate,
                    },
                  });

                  // address
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'address',
                      ],
                      'type': 'address/magnes-v1',
                      'content': {
                        'street': this.street,
                        'town': this.town,
                        'zip': this.zip,
                        'country': this.country,
                      },
                    },
                  });

                  // height
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'height',
                      ],
                      'type': 'length/cm',
                      'content': Math.round((this.units === 'imperial') ? 30.48 * this.heightFeet + 2.54 * this.heightInches : this.height),
                    },
                  });
                  // weight
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'weight',
                      ],
                      'type': 'mass/kg',
                      'content': Math.round((this.units === 'imperial') ? this.weight * 0.4536 : this.weight),
                    },
                  });

                  // shoe-size
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'shoe-size',
                      ],
                      'type': 'length/shoe-size-eu',
                      'content': this.shoesize,
                    },
                  });

                  // impaired side
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'impaired-side',
                      ],
                      'type': 'attributes/impaired-side',
                      'content': this.impairedsideSelection('en')[this.impairedsideSelection(this.ctx.language).findIndex(element => { return element === this.impairedside; })],
                    },
                  });

                  // biological sex
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'sex',
                      ],
                      'type': 'attributes/biological-sex',
                      'content': this.biologicalsexSelection('en')[this.biologicalsexSelection(this.ctx.language).findIndex(element => { return element === this.biologicalsex; })],
                    },
                  });

                  // phone number
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'phone',
                      ],
                      'type': 'call/telephone',
                      'content': this.phoneno,
                    },
                  });

                  // development consent
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'development-ok',
                      ],
                      'type': 'boolean/temp',
                      'content': this.developmentok === 'yes',
                    },
                  });
                } else if (this.userType.id === 'doctor') {
                  calls = this.setupCalls.doctor;

                  // name
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'name',
                      ],
                      'type': 'contact/magnes-v1',
                      'content': {
                        'first-name': this.firstname,
                        'middle-name': '',
                        'last-name': this.lastname,
                      },
                    },
                  });

                  // address
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'address',
                      ],
                      'type': 'address/magnes-v1',
                      'content': {
                        'street': this.street,
                        'town': this.town,
                        'zip': this.zip,
                        'country': this.country,
                      },
                    },
                  });

                  // phone number
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'phone',
                      ],
                      'type': 'call/telephone',
                      'content': this.phoneno,
                    },
                  });
                } else if (this.userType.id === 'analysis') {
                  calls = this.setupCalls.analysis;
                } else if (this.userType.id === 'statistician') {
                  calls = this.setupCalls.statistician;
                } else if (this.userType.id === 'nushu_x_user') {
                  calls = this.setupCalls.nushu_x_user;

                  // name
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'name',
                      ],
                      'type': 'contact/magnes-v1',
                      'content': {
                        'first-name': this.firstname,
                        'last-name': this.lastname,
                      },
                    },
                  });

                  // birthdate
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'birthdate',
                      ],
                      'type': 'date/iso-8601',
                      'content': this.birthdate,
                    },
                  });

                  // height
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'height',
                      ],
                      'type': 'length/cm',
                      'content': Math.round((this.units === 'imperial') ? 30.48 * this.heightFeet + 2.54 * this.heightInches : this.height),
                    },
                  });

                  // weight
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'weight',
                      ],
                      'type': 'mass/kg',
                      'content': Math.round((this.units === 'imperial') ? this.weight * 0.4536 : this.weight),
                    },
                  });

                  // biological sex
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'sex',
                      ],
                      'type': 'attributes/biological-sex',
                      'content': this.biologicalsexSelection('en')[this.biologicalsexSelection(this.ctx.language).findIndex(element => { return element === this.biologicalsex; })],
                    },
                  });

                  // development consent
                  calls.push({
                    'method': 'events.create',
                    'params': {
                      'streamIds': [
                        'development-ok',
                      ],
                      'type': 'boolean/temp',
                      'content': this.developmentok === 'yes',
                    },
                  });
                }
                // use the connection that is opened by `login` to issue a batch
                // call of all the stream creations.
                try {
                  connection.api(calls);
                } catch (err) {
                  this.showError(err);
                }
              });
          } catch (err) {
            this.showError(err);
          };
        })
        .catch(async (err) => {
          if (err.msg.toLowerCase().includes('an user with username') && err.msg.toLowerCase().includes('already exists')) {
            this.createUniqueUser();
          } else {
            this.showError(err);
          }
        })
        .finally(() => { this.submitting = false; });
    },
    clear () {
      this.$refs.form.reset();
    },
    saveBirthdate (date) {
      this.$refs.menu.save(date);
    },
    initHostings (hostings) {
      this.availableHostings = hostings;
    },
    showError (error) {
      this.error = error.msg || error.message;
    },
    generateRandomUsername (length) {
      let result = '';
      switch (this.userType.id) {
        case 'doctor':
          result += 'd-';
          break;
        case 'patient':
          result += 'p-';
          break;
        case 'nushu_x_user':
          result += 'x-';
          break;
        default:
          result += 'u-';
          break;
      }

      let characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
      let charactersLength = characters.length;
      for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
      }

      if (this.userType.id === 'analysis') {
        result = 'analyst';
      }

      if (this.userType.id === 'statistician') {
        result = 'statistician';
      }

      return result;
    },
    getAge (dateString) {
      var today = new Date();
      var birthDate = new Date(dateString);
      var age = today.getFullYear() - birthDate.getFullYear();
      var m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    },
    getAffiliation () {
      switch (this.userType.id) {
        case 'doctor':
          return this.readAffiliationFromTxtField();
        case 'nushu_x_user':
          return 'NushuX';
        default:
          return 'Magnes';
      }
    },
    readAffiliationFromTxtField () {
      try {
        return Object.entries(affiliations)
          .filter(([key, value]) => value.name === this.affiliation)[0][0];
      } catch (e) {
        console.warn(e);
        return 'Magnes';
      }
    },
  },
};

</script>



// WEBPACK FOOTER //
// src/components/views/RegisterUser.vue